import axios from 'axios';

interface SeedDataResult {
    success: boolean;
    message: string;
    errors?: string[];
}

export const seedMasterData = async (): Promise<SeedDataResult> => {
    const errors: string[] = [];

    try {
        console.log('🌱 Starting master data seeding...');

        // 1. Create Currency
        try {
            await axios.post('/api/master-data/currency', {
                name: 'Indian Rupee',
                code: 'INR',
                symbol: '₹',
                decimalPlaces: 2,
                isBaseCurrency: true,
                exchangeRate: 1,
                status: 'ACTIVE'
            });
            console.log('✅ Currency INR created');
        } catch (error: any) {
            if (!error.response?.data?.error?.message?.includes('already exists')) {
                errors.push(`Currency creation failed: ${error.response?.data?.error?.message || error.message}`);
            }
        }

        // 2. Create Country
        try {
            await axios.post('/api/master-data/country', {
                name: 'India',
                code: 'IN',
                iso2Code: 'IN',
                iso3Code: 'IND',
                dialCode: '+91',
                currency: 'INR',
                timezone: 'Asia/Kolkata',
                region: 'Asia',
                isActive: true,
                status: 'ACTIVE'
            });
            console.log('✅ Country India created');
        } catch (error: any) {
            if (!error.response?.data?.error?.message?.includes('already exists')) {
                errors.push(`Country creation failed: ${error.response?.data?.error?.message || error.message}`);
            }
        }

        // 3. Create Company
        let companyId: string;
        try {
            const companyResponse = await axios.post('/api/master-data/company', {
                name: 'Demo Courier Company',
                email: 'admin@democourier.com',
                contactNo: '+91-9876543210',
                faxNo: '+91-22-12345678',
                website: 'https://democourier.com',
                trackingUrl: 'https://track.democourier.com',
                redirectUrl: 'https://democourier.com',
                panNumber: 'DEMCO1234P',
                taxType: 'GST',
                gstVatNo: '27DEMCO1234P1Z5',
                sacCode: '996511',
                courierRegistrationNumber: 'CR123456789',
                awbNoPrefix: 'DC',
                address: 'Demo Business Park',
                city: 'Mumbai',
                state: 'Maharashtra',
                country: 'India',
                pincode: '400001',
                status: 'ACTIVE',
                billingConfig: {
                    defaultTaxRate: 18,
                    sacCode: '996511',
                    enableGst: true,
                    gstRegistrationNumber: '27DEMCO1234P1Z5'
                }
            });
            companyId = companyResponse.data.data._id;
            console.log('✅ Demo Company created');
        } catch (error: any) {
            if (error.response?.data?.error?.message?.includes('already exists')) {
                // Get existing company
                const existingCompany = await axios.get('/api/master-data/company');
                companyId = existingCompany.data.data[0]?._id;
            } else {
                errors.push(`Company creation failed: ${error.response?.data?.error?.message || error.message}`);
                return { success: false, message: 'Failed to create company', errors };
            }
        }

        // 4. Get currency ID
        let currencyId: string;
        try {
            const currencyResponse = await axios.get('/api/master-data/currency');
            const inrCurrency = currencyResponse.data.data.find((c: any) => c.code === 'INR');
            currencyId = inrCurrency?._id;
            if (!currencyId) {
                errors.push('INR currency not found');
                return { success: false, message: 'Currency not found', errors };
            }
        } catch (error: any) {
            errors.push(`Failed to fetch currency: ${error.message}`);
            return { success: false, message: 'Failed to fetch currency', errors };
        }

        // 5. Create Hub
        try {
            await axios.post('/api/master-data/hub', {
                name: 'Mumbai Main Hub',
                code: 'MUM01',
                hubType: 'ORIGIN',
                company: companyId,
                address: 'Plot No. 123, Logistics Park, Andheri East',
                city: 'Mumbai',
                state: 'Maharashtra',
                country: 'India',
                pincode: '400093',
                contactNo: '+91-9876543210',
                email: 'mumbai@democourier.com',
                managerName: 'Rajesh Kumar',
                managerContact: '+91-9876543211',
                managerEmail: 'rajesh.kumar@democourier.com',
                timezone: 'Asia/Kolkata',
                currency: currencyId,
                maxCapacity: 10000,
                currentLoad: 0,
                facilities: ['Warehouse', 'Loading Dock', 'Sorting Facility', 'Security'],
                status: 'ACTIVE',
                operationalHours: {
                    monday: { open: '09:00', close: '18:00', isOpen: true },
                    tuesday: { open: '09:00', close: '18:00', isOpen: true },
                    wednesday: { open: '09:00', close: '18:00', isOpen: true },
                    thursday: { open: '09:00', close: '18:00', isOpen: true },
                    friday: { open: '09:00', close: '18:00', isOpen: true },
                    saturday: { open: '09:00', close: '14:00', isOpen: true },
                    sunday: { open: '10:00', close: '16:00', isOpen: false }
                }
            });
            console.log('✅ Mumbai Hub created');
        } catch (error: any) {
            if (!error.response?.data?.error?.message?.includes('already exists')) {
                errors.push(`Hub creation failed: ${error.response?.data?.error?.message || error.message}`);
            }
        }

        // 6. Create Vendor
        try {
            await axios.post('/api/master-data/vendor', {
                name: 'Express Logistics Pvt Ltd',
                code: 'ELP01',
                vendorType: 'COURIER',
                vendorCategory: 'DOMESTIC',
                company: companyId,
                contactPerson: 'Amit Sharma',
                contactNo: '+91-9876543220',
                email: 'amit@expresslogistics.com',
                alternateEmail: 'support@expresslogistics.com',
                website: 'https://expresslogistics.com',
                address: '456 Transport Nagar, Goregaon West',
                city: 'Mumbai',
                state: 'Maharashtra',
                country: 'India',
                pincode: '400062',
                gstNumber: '27EXPLOG1234A1Z5',
                panNumber: 'EXPLOG1234A',
                creditLimit: 500000,
                creditDays: 30,
                paymentTerms: '30 days net payment from invoice date',
                paymentMethod: 'ONLINE',
                tdsPercentage: 2,
                performanceRating: 4.5,
                status: 'ACTIVE'
            });
            console.log('✅ Vendor created');
        } catch (error: any) {
            if (!error.response?.data?.error?.message?.includes('already exists')) {
                errors.push(`Vendor creation failed: ${error.response?.data?.error?.message || error.message}`);
            }
        }

        // 7. Create Customer
        try {
            await axios.post('/api/master-data/customer', {
                name: 'ABC Electronics Ltd',
                email: 'orders@abcelectronics.com',
                contactNo: '+91-9876543230',
                company: companyId,
                customerType: 'DIRECT',
                customerCategory: 'COMPANY',
                status: 'ACTIVE',
                city: 'Mumbai',
                state: 'Maharashtra',
                country: 'India',
                creditLimit: 100000,
                gstNumber: '27ABCEL1234B1Z5',
                address: '789 Business Complex, Bandra Kurla Complex',
                pincode: '400051'
            });
            console.log('✅ Customer created');
        } catch (error: any) {
            if (!error.response?.data?.error?.message?.includes('already exists')) {
                errors.push(`Customer creation failed: ${error.response?.data?.error?.message || error.message}`);
            }
        }

        if (errors.length === 0) {
            console.log('🎉 Master data seeding completed successfully!');
            return {
                success: true,
                message: 'Master data seeded successfully! You can now test the functionality.'
            };
        } else {
            console.log('⚠️ Master data seeding completed with some errors:', errors);
            return {
                success: true,
                message: 'Master data seeded with some warnings. Most data should be available for testing.',
                errors
            };
        }

    } catch (error: any) {
        console.error('❌ Master data seeding failed:', error);
        return {
            success: false,
            message: 'Failed to seed master data',
            errors: [error.message]
        };
    }
};

// Helper function to clear all master data (for testing)
export const clearMasterData = async (): Promise<SeedDataResult> => {
    const errors: string[] = [];
    const entities = ['customer', 'vendor', 'hub', 'company', 'country', 'currency'];

    try {
        console.log('🧹 Clearing master data...');

        for (const entity of entities) {
            try {
                const response = await axios.get(`/api/master-data/${entity}`);
                const items = response.data.data;

                for (const item of items) {
                    try {
                        await axios.delete(`/api/master-data/${entity}/${item._id}`);
                    } catch (deleteError: any) {
                        errors.push(`Failed to delete ${entity} ${item._id}: ${deleteError.message}`);
                    }
                }
                console.log(`✅ Cleared ${entity} data`);
            } catch (error: any) {
                errors.push(`Failed to fetch ${entity} data: ${error.message}`);
            }
        }

        return {
            success: errors.length === 0,
            message: errors.length === 0 ? 'Master data cleared successfully' : 'Master data cleared with some errors',
            errors: errors.length > 0 ? errors : undefined
        };
    } catch (error: any) {
        return {
            success: false,
            message: 'Failed to clear master data',
            errors: [error.message]
        };
    }
};

// Make functions available in browser console for testing
if (typeof window !== 'undefined') {
    (window as any).seedMasterData = seedMasterData;
    (window as any).clearMasterData = clearMasterData;
} 